import { createContext, useContext } from "react";

export type AnalyticsClient = {
  VIEW_PUSH_DELAY: number;
  viewedProductQueue: Record<string, any>[];
  viewedProductPushTimeout: ReturnType<typeof setTimeout> | null;
  viewedProductVisibilityChangeHandled: boolean;
  emit(event: Record<string, any>): void;
  unpublishedViewedProducts(): Record<string, any>[];
  pushViewedProducts(): void;
  productViewed(product: Record<string, any>): void;
};

export type Volume = {
  (target: string): VolumeClient;
  selectAllEmbeds(): NodeListOf<Element>;
  install(host: string): void;
  uninstall(): void;
  reportEvent(eventData: any): void;
  log(eventData: any): void;
  trackView(el: HTMLIFrameElement, placement?: string): void;
  postToAll(
    message: string | Record<string, any>,
    value?: string,
    excludeSrc?: Window,
  ): void;
  stopAll(): void;
};

export type VolumeClient = {
  play(opts: { request: any }): VolumeClient;
  pause(opts: { request: any }): VolumeClient;
  post(message: string | Record<string, any>, value?: any): VolumeClient;
  request(message: string, value?: any, callback?: () => void): VolumeClient;
};

export interface AnalyticsContextType {
  client: AnalyticsClient;
  volume: Volume;
  subXTrackingEnabled: boolean;
}

const AnalyticsContext = createContext<AnalyticsContextType | null>(null);

export const useAnalytics = (): AnalyticsClient | null => {
  const context = useContext(AnalyticsContext);

  if (!context) {
    return null;
  }

  return context.client;
};

export const useVolume = (): Volume | null => {
  const context = useContext(AnalyticsContext);

  if (!context) {
    return null;
  }

  return context.volume;
};

export const useSubXTracking = (): boolean | null => {
  const context = useContext(AnalyticsContext);

  if (!context) {
    return null;
  }

  return context.subXTrackingEnabled;
};

export default AnalyticsContext;
